import { useState, useEffect } from 'react'
import {
  MdLuggage,
  MdAdd,
  MdRefresh,
  MdPerson,
  MdFlight,
  MdBusiness,
  MdScale,
  MdStraighten,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  LoadingSpinner,
  SearchBar,
  FilterBar,
  StatCard,
  EmptyState,
} from '../../components/ui'
import { useBaggage } from '../../hooks/useBaggage'
import { useWebsocket } from '../../hooks/useWebsocket'
import BaggageList from '../../modules/baggage/BaggageList'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function BaggagePage() {
  const dispatch = useDispatch()
  const [baggage, setBaggage] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [flightFilter, setFlightFilter] = useState('all')
  const [airlineFilter, setAirlineFilter] = useState('all')
  const { getBaggageListAsync, isGetBaggageListLoading } = useBaggage()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    total: 0,
    totalWeight: 0,
    averageWeight: 0,
    flights: 0,
    airlines: 0,
    passengers: 0,
  })

  const loadBaggage = async () => {
    try {
      setIsLoading(true)
      const baggageData = await getBaggageListAsync()
      setBaggage(baggageData || [])

      // Calculate statistics
      const total = baggageData.length
      const totalWeight = baggageData.reduce((sum, item) => sum + (item.weight || 0), 0)
      const averageWeight = total > 0 ? totalWeight / total : 0

      // Get unique flights, airlines, and passengers
      const uniqueFlights = new Set(baggageData.map((item) => item.flight?._id).filter(Boolean))
      const uniqueAirlines = new Set(
        baggageData.map((item) => item.flight?.airline?._id).filter(Boolean),
      )
      const uniquePassengers = new Set(
        baggageData.map((item) => item.passenger?._id).filter(Boolean),
      )

      setStats({
        total,
        totalWeight,
        averageWeight,
        flights: uniqueFlights.size,
        airlines: uniqueAirlines.size,
        passengers: uniquePassengers.size,
      })
    } catch (error) {
      console.error('Error loading baggage:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadBaggage()

    const channelName = getWebsocketChannel().baggageList

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredBaggage = () => {
    let filtered = baggage

    // Flight filter
    if (flightFilter !== 'all') {
      filtered = filtered.filter((item) => item.flight?.number === flightFilter)
    }

    // Airline filter
    if (airlineFilter !== 'all') {
      filtered = filtered.filter((item) => item.flight?.airline?.name === airlineFilter)
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.passenger?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.passenger?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.flight?.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.flight?.airline?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.tagNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    return filtered
  }

  const filteredBaggage = getFilteredBaggage()

  // Get unique flights and airlines for filters
  const uniqueFlights = [...new Set(baggage.map((item) => item.flight?.number).filter(Boolean))]
  const uniqueAirlines = [
    ...new Set(baggage.map((item) => item.flight?.airline?.name).filter(Boolean)),
  ]

  const handleAddBaggage = () => {
    navigate('/baggages/new')
  }

  const handleRefresh = () => {
    loadBaggage()
  }

  const handleViewBaggage = (baggage) => {
    navigate(`/baggages/${baggage._id}`)
  }

  const handleEditBaggage = (baggage) => {
    navigate(`/baggages/${baggage._id}/edit`)
  }

  const handleDeleteBaggage = (baggage) => {
    // TODO: Implement delete confirmation modal
    console.log('Delete baggage:', baggage._id)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              Baggage Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Monitor and manage baggage handling and tracking operations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetBaggageListLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetBaggageListLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleAddBaggage}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Baggage</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Baggage"
          value={stats.total}
          subtitle="Items tracked"
          icon={MdLuggage}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />
        <StatCard
          title="Total Weight"
          value={`${stats.totalWeight.toFixed(1)} kg`}
          subtitle="Combined weight"
          icon={MdScale}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />
        <StatCard
          title="Average Weight"
          value={`${stats.averageWeight.toFixed(1)} kg`}
          subtitle="Per item"
          icon={MdStraighten}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
        <StatCard
          title="Active Flights"
          value={stats.flights}
          subtitle="With baggage"
          icon={MdFlight}
          iconBgColor="bg-info-100 dark:bg-info-900"
          iconColor="text-info-600 dark:text-info-400"
        />
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <StatCard
          title="Airlines"
          value={stats.airlines}
          subtitle="Handling baggage"
          icon={MdBusiness}
          iconBgColor="bg-purple-100 dark:bg-purple-900"
          iconColor="text-purple-600 dark:text-purple-400"
        />
        <StatCard
          title="Passengers"
          value={stats.passengers}
          subtitle="With checked baggage"
          icon={MdPerson}
          iconBgColor="bg-indigo-100 dark:bg-indigo-900"
          iconColor="text-indigo-600 dark:text-indigo-400"
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Baggage ({filteredBaggage.length})
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search baggage..."
            />
            <div className="flex items-center space-x-3">
              <FilterBar
                filterValue={flightFilter}
                onFilterChange={(e) => setFlightFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Flights' },
                  ...uniqueFlights.map((flight) => ({
                    value: flight,
                    label: flight,
                  })),
                ]}
              />
              <FilterBar
                filterValue={airlineFilter}
                onFilterChange={(e) => setAirlineFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Airlines' },
                  ...uniqueAirlines.map((airline) => ({
                    value: airline,
                    label: airline,
                  })),
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Baggage List */}
      {filteredBaggage.length === 0 && !isLoading ? (
        <EmptyState
          icon={MdLuggage}
          title="No baggage found"
          description={
            searchTerm || flightFilter !== 'all' || airlineFilter !== 'all'
              ? 'No baggage matches your current filters. Try adjusting your search criteria.'
              : 'Get started by adding your first baggage item to track.'
          }
          action={
            searchTerm || flightFilter !== 'all' || airlineFilter !== 'all' ? (
              <Button
                variant="primary"
                onClick={() => {
                  setSearchTerm('')
                  setFlightFilter('all')
                  setAirlineFilter('all')
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={handleAddBaggage}>
                Add Baggage
              </Button>
            )
          }
        />
      ) : (
        <BaggageList
          baggage={filteredBaggage}
          onViewBaggage={handleViewBaggage}
          onEditBaggage={handleEditBaggage}
          onDeleteBaggage={handleDeleteBaggage}
        />
      )}
    </div>
  )
}

export default BaggagePage
